import { ReactNode, useMemo, useState } from 'react';
import { CreatePaymentIntentBody } from '@frontend/api-weave-pay';
import { contextFactory } from '@frontend/design-system';

interface StripeStepperContextProps {
  captchaToken: string | null;
  setCaptchaToken: (token: string | null) => void;
  paymentRequest: CreatePaymentIntentBody | undefined;
  setPaymentRequest: (paymentIntentBody: CreatePaymentIntentBody | undefined) => void;
  portalStepperSettings: PortalStepperSettings;
  setPortalStepperSettings: (settings: PortalStepperSettings) => void;
}

const [StripeStepperContext, useStripeStepperContext] = contextFactory<StripeStepperContextProps>();

interface StripeStepperProviderProps {
  children: ReactNode;
}

type PortalStepperSettings = {
  allowSaveACHOnFile: boolean;
  allowSaveCardOnFile: boolean;
  amountEditable: boolean;
  isLive: boolean;
  oneTimeRequest: boolean;
  showExpressElement: boolean;
  skipAttachmentAuth: boolean;
  skipCaptcha: boolean;
};

export const StripeStepperProvider = ({ children }: StripeStepperProviderProps) => {
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [paymentRequest, setPaymentRequest] = useState<CreatePaymentIntentBody | undefined>();
  const [portalStepperSettings, setPortalStepperSettings] = useState<PortalStepperSettings>({
    allowSaveACHOnFile: false,
    allowSaveCardOnFile: false,
    amountEditable: true,
    isLive: true,
    oneTimeRequest: false,
    showExpressElement: false,
    skipAttachmentAuth: false,
    skipCaptcha: false,
  });

  const value = useMemo(
    () => ({
      paymentRequest,
      setPaymentRequest,
      portalStepperSettings,
      setPortalStepperSettings,
      captchaToken,
      setCaptchaToken,
    }),
    [paymentRequest, portalStepperSettings, captchaToken]
  );

  return <StripeStepperContext.Provider value={value}>{children}</StripeStepperContext.Provider>;
};

export const useStripeStepper = () => {
  const stripeStepperContext = useStripeStepperContext();

  if (!stripeStepperContext) {
    throw new Error('useStripeStepper must be used within a StripeStepperProvider');
  }

  return stripeStepperContext;
};
